// Onboarding state machine — drives the funnel per `docs/idea/onboarding.md`.
//
// Linear path (arc 101 slice 02 — plan pick + BYOK key precede the GH App install;
// #3632 — a box before the repo pick, a chat client before ACTIVE):
//   NEW → AUTHED → PLAN_SELECTED → KEY_SET → GH_CONNECTED → BOX_ATTACHED → REPOS_SELECTED
//     → YML_REQUESTED → MCP_CONNECTED → ACTIVE
//                    ↘ INVALID ↙        (retry → YML_REQUESTED)
//
// WHY THE TWO #3632 RUNGS SIT WHERE THEY DO. The setup pass that authors the
// `dz/onboarding` pull request (the `.maintainer.yml` the yml step waits on) runs
// on the CUSTOMER's box, so a repo pick with no enrolled runner requests a file
// nothing can produce — `BOX_ATTACHED` gates the pick. And `ACTIVE` is terminal:
// it is the platform's only "this tenant is done" signal, and a tenant with no
// chat client connected has no way to drive the product it just onboarded onto
// (prod 2026-09-10: the wizard never asked for either, and the one org with 7
// boxes, 2 org keys and 1 install was still `new`). So the yml verdict lands
// `ACTIVE` only from `MCP_CONNECTED`; from `YML_REQUESTED` a `validated` verdict
// is DEFERRED (see {@link ymlVerdictEvents}) and re-derived from the durable
// policy snapshot once the rung is settled, while an `invalid` verdict is taken
// immediately from either — a refusal that names the broken repo is never
// delayed behind an unrelated step.
//
// Pure: no I/O. The dashboard + API call `advance(state, event)` to decide
// what state to persist next on `Account.onboardingState`. Reaching `ACTIVE`
// is terminal — onboarding is over forever (per `onboarding.md` § Steady state).

import { z } from 'zod';
import { OnboardingState, type OnboardingState as OnboardingStateT } from '../entities/account';

/**
 * Discrete events that move the funnel forward. One per UI step + the two
 * validation outcomes after a `.maintainer.yml` lands on a repo's default branch
 * — merged from the pass's `dz/onboarding` pull request, or committed by hand.
 *
 * Step→event mapping:
 *   step 1 (auth)              → `auth_completed`
 *   step 2 (single/team pick)  → `plan_selected`
 *   step 3 (BYOK saved)        → `key_set`
 *   step 4 (GH App install)    → `gh_app_installed`
 *   step 5 (box enrolled)      → `box_attached`
 *   step 6 (repo selection)    → `repos_selected`
 *   step 7 (setup PR seen)     → `yml_requested`
 *   step 8 (chat client)       → `mcp_connected`
 *   step 9 (validation)        → `yml_validated` | `yml_invalid`
 *   step 9 (retry after fix)   → `yml_retried`
 */
export const OnboardingEvent = {
  AuthCompleted: 'auth_completed',
  PlanSelected: 'plan_selected',
  KeySet: 'key_set',
  GhAppInstalled: 'gh_app_installed',
  /** The account holds at least one enrolled (non-`pending`, non-`revoked`) runner. */
  BoxAttached: 'box_attached',
  ReposSelected: 'repos_selected',
  YmlRequested: 'yml_requested',
  /** The account holds at least one live human-bound API token — a chat client connected. */
  McpConnected: 'mcp_connected',
  YmlValidated: 'yml_validated',
  YmlInvalid: 'yml_invalid',
  YmlRetried: 'yml_retried',
} as const;
export const OnboardingEventSchema = z.enum(OnboardingEvent);
export type OnboardingEvent = z.infer<typeof OnboardingEventSchema>;

/**
 * Static transition table. `undefined` ⇒ no valid transition from that pair.
 * Frozen so callers can't accidentally mutate.
 */
const TRANSITIONS: Readonly<
  Record<OnboardingStateT, Partial<Record<OnboardingEvent, OnboardingStateT>>>
> = Object.freeze({
  [OnboardingState.New]: { [OnboardingEvent.AuthCompleted]: OnboardingState.Authed },
  [OnboardingState.Authed]: { [OnboardingEvent.PlanSelected]: OnboardingState.PlanSelected },
  [OnboardingState.PlanSelected]: { [OnboardingEvent.KeySet]: OnboardingState.KeySet },
  [OnboardingState.KeySet]: { [OnboardingEvent.GhAppInstalled]: OnboardingState.GhConnected },
  [OnboardingState.GhConnected]: { [OnboardingEvent.BoxAttached]: OnboardingState.BoxAttached },
  [OnboardingState.BoxAttached]: {
    [OnboardingEvent.ReposSelected]: OnboardingState.ReposSelected,
  },
  [OnboardingState.ReposSelected]: { [OnboardingEvent.YmlRequested]: OnboardingState.YmlRequested },
  [OnboardingState.YmlRequested]: {
    [OnboardingEvent.McpConnected]: OnboardingState.McpConnected,
    // `yml_validated` is deliberately ABSENT here: ACTIVE is reachable only
    // through MCP_CONNECTED (module header). The refusal is not deferred.
    [OnboardingEvent.YmlInvalid]: OnboardingState.Invalid,
  },
  [OnboardingState.McpConnected]: {
    [OnboardingEvent.YmlValidated]: OnboardingState.Active,
    [OnboardingEvent.YmlInvalid]: OnboardingState.Invalid,
  },
  [OnboardingState.Invalid]: {
    // Lands on YML_REQUESTED whichever of its two origins it came from: the
    // weaker state is the honest one, and the MCP rung re-settles from the
    // durable token fact on the next read (the ladder in `apps/api`).
    [OnboardingEvent.YmlRetried]: OnboardingState.YmlRequested,
  },
  // ACTIVE is terminal — no events accepted. Onboarding is done forever
  // (see `onboarding.md` § Steady state).
  [OnboardingState.Active]: {},
});

/**
 * WHO can witness each event — the partition that decides whether the
 * CALLER-FACING door may fire it (`POST /v1/onboarding/advance`, MCP
 * `config onboarding_advance`; both reach `advanceOnboarding` in `apps/api`).
 *
 * - `caller` — the caller is the only witness. "I picked a plan", "I selected
 *   repos", "I have the setup PR", "I fixed the file". Nothing durable proves
 *   these and nothing needs to: they record an intent, and a caller who lies
 *   about their own intent has only misled themselves.
 * - `platform` — the PLATFORM is the witness, and the caller is the subject. A
 *   key verified against the provider, an App installation delivered by GitHub,
 *   a `.maintainer.yml` this system parsed. The subject of an observation must
 *   not be able to assert it.
 *
 * The distinction is load-bearing because {@link advance} is a pure transition
 * table with no notion of evidence: a caller holding `write:account` could fire
 * seven events in order and land on `ACTIVE` — which is TERMINAL — with no key,
 * no installation and no policy file. That is worse than being stuck, because
 * the funnel is the platform's only progress signal and nothing walks a terminal
 * state back.
 *
 * This is not the only way that divergence happens — the install webhook's
 * account writer stamped a new account `repos_selected` on INSERT (#2400 root
 * cause 4; every account is now created at `new` by `upsertAccountByGithubId`,
 * #3841), and nothing walks `ACTIVE` back when its evidence later
 * evaporates. Prod on 2026-08-25 held one of each shape and no trail saying
 * which mechanism produced either, the funnel's audit sink having been a
 * `console.log` until #2402. So this closes one door of several.
 *
 * Every platform-witnessed event has a real producer that does NOT come through
 * the caller door, so closing that door removes no working path:
 * `key_set` from the upstream settle ladder alone, once the org pool holds a
 * `valid` key (#4613 deleted the verify path's writer), `gh_app_installed` from
 * the install webhook (`driveOnboardingEvent`) and that ladder, `yml_validated` /
 * `yml_invalid` from the policy ingest (`driveOnboardingYmlVerdict`) and the yml
 * settle. Each reaches {@link advance} plus the store directly.
 */
export const OnboardingEventWitness = {
  Caller: 'caller',
  Platform: 'platform',
} as const;
export type OnboardingEventWitness =
  (typeof OnboardingEventWitness)[keyof typeof OnboardingEventWitness];

/**
 * The partition itself. A total `Record` on purpose: adding a member to
 * {@link OnboardingEvent} without classifying it is a TYPE ERROR, so the guard
 * cannot silently fall open on an event nobody thought about.
 */
const EVENT_WITNESS: Readonly<Record<OnboardingEvent, OnboardingEventWitness>> = Object.freeze({
  // Reaching the door at all means a credential resolved to this account, so
  // this is the one caller event the platform could also witness. It stays a
  // caller event because the dashboard's step 1 fires it and nothing turns on it.
  [OnboardingEvent.AuthCompleted]: OnboardingEventWitness.Caller,
  [OnboardingEvent.PlanSelected]: OnboardingEventWitness.Caller,
  [OnboardingEvent.KeySet]: OnboardingEventWitness.Platform,
  [OnboardingEvent.GhAppInstalled]: OnboardingEventWitness.Platform,
  // A runner row past `pending` is written by the enroll call, a live human
  // token by the mint — both platform records; the caller is their subject.
  [OnboardingEvent.BoxAttached]: OnboardingEventWitness.Platform,
  [OnboardingEvent.ReposSelected]: OnboardingEventWitness.Caller,
  [OnboardingEvent.YmlRequested]: OnboardingEventWitness.Caller,
  [OnboardingEvent.McpConnected]: OnboardingEventWitness.Platform,
  [OnboardingEvent.YmlValidated]: OnboardingEventWitness.Platform,
  [OnboardingEvent.YmlInvalid]: OnboardingEventWitness.Platform,
  // A retry is the caller saying "I fixed it"; the VERDICT on the fix is still
  // the platform's, and that verdict is the pair above.
  [OnboardingEvent.YmlRetried]: OnboardingEventWitness.Caller,
});

/** Who witnesses `event` — see {@link OnboardingEventWitness}. */
export function witnessFor(event: OnboardingEvent): OnboardingEventWitness {
  return EVENT_WITNESS[event];
}

/**
 * The producer that legitimately fires a platform-witnessed event, for the
 * refusal text. Says what will move the funnel instead of only what will not —
 * a refusal an agent can act on, rather than a wall.
 */
const PLATFORM_EVENT_PRODUCER: Readonly<Partial<Record<OnboardingEvent, string>>> = Object.freeze({
  [OnboardingEvent.KeySet]:
    'verify a BYOK key for this account (the funnel advances when the provider accepts it)',
  [OnboardingEvent.GhAppInstalled]:
    'install the GitHub App on the org (the install webhook advances the funnel)',
  [OnboardingEvent.BoxAttached]:
    'enroll a runner for this account (the funnel advances once a box has dialed in)',
  [OnboardingEvent.McpConnected]:
    'connect a chat client — mint a personal API token or complete an OAuth grant (the funnel advances on the live token)',
  [OnboardingEvent.YmlValidated]:
    'commit a valid .maintainer.yml to an enabled repo (the policy ingest advances the funnel)',
  [OnboardingEvent.YmlInvalid]:
    'commit a .maintainer.yml to an enabled repo (the policy ingest records the verdict, valid or not)',
});

/**
 * What to do instead of firing `event` through the caller door, or `null` when
 * the event is caller-witnessed and needs no alternative.
 */
export function producerForPlatformEvent(event: OnboardingEvent): string | null {
  return PLATFORM_EVENT_PRODUCER[event] ?? null;
}

export type AdvanceResult =
  | { ok: true; state: OnboardingStateT }
  | { ok: false; reason: 'invalid-transition'; from: OnboardingStateT; event: OnboardingEvent };

/**
 * Compute the next onboarding state for a given event. Pure — caller persists
 * the result on `Account.onboardingState`.
 *
 * Returns `{ ok: false, reason: 'invalid-transition' }` when the event isn't
 * allowed from the current state (e.g. firing `key_set` while still `NEW`,
 * or any event after `ACTIVE`). Callers map this to HTTP 409 in the API.
 */
export function advance(from: OnboardingStateT, event: OnboardingEvent): AdvanceResult {
  const next = TRANSITIONS[from][event];
  if (next === undefined) return { ok: false, reason: 'invalid-transition', from, event };
  return { ok: true, state: next };
}

/**
 * What moves the funnel next from a given state, and WHO can cause it.
 *
 * A discriminated verdict rather than an `OnboardingEvent | null`, because that
 * return type was a trap on both ends (arc 101 slice 05, #4611):
 *
 *  - it named a PLATFORM-witnessed event as "the next step" from five states,
 *    and the caller door refuses every one of them from every state
 *    ({@link witnessFor} / `advanceOnboarding`'s `not_caller_witnessed`). An
 *    agent reading it and firing it got a refusal it could not retry its way
 *    out of — and `config onboarding_advance`'s own field description pointed
 *    at this value.
 *  - its `null` collapsed two opposite facts. `ACTIVE` is TERMINAL: nothing is
 *    next, ever. `MCP_CONNECTED` is WAITING: the policy ingest's verdict is
 *    coming. A reader that cannot tell them apart either polls a finished
 *    tenant forever or gives up on a live one.
 *
 * The arms are the three answers, and each says what to DO:
 *  - `caller` — fire `event` through the caller door.
 *  - `platform_witnessed` — the platform observes `waiting_on`; `instead` is the
 *    act that causes the observation ({@link producerForPlatformEvent}).
 *  - `complete` — the funnel is over (⇔ {@link isTerminal}).
 */
export type NextFunnelMove =
  | { kind: 'caller'; event: OnboardingEvent }
  | { kind: 'platform_witnessed'; waiting_on: OnboardingEvent; instead: string }
  | { kind: 'complete' };

/**
 * The legal events from `state`, minus the funnel's REFUSAL edges — the hops
 * that land on `INVALID`. A refusal is never a move anyone is waiting for: it
 * is the policy ingest recording that a `.maintainer.yml` did not parse, and
 * the linear path in the module header puts `INVALID` off to the side for
 * exactly that reason.
 *
 * Falls back to the unfiltered set rather than answering empty, so a state whose
 * ONLY exit is a refusal (none today — `YML_REQUESTED` and `MCP_CONNECTED` each
 * pair theirs with a forward hop) still reports what it is waiting on instead of
 * claiming to be terminal. `onboarding.test.ts` pins that every non-terminal
 * state yields exactly one forward event, which is what makes the `[0]` below a
 * choice rather than a coin flip.
 */
function forwardEvents(state: OnboardingStateT): OnboardingEvent[] {
  const legal = Object.keys(TRANSITIONS[state]) as OnboardingEvent[];
  const forward = legal.filter((e) => TRANSITIONS[state][e] !== OnboardingState.Invalid);
  return forward.length > 0 ? forward : legal;
}

/**
 * What the funnel is waiting on from `state` — see {@link NextFunnelMove}.
 *
 * DERIVED from {@link TRANSITIONS} and {@link EVENT_WITNESS}, never a hand-written
 * switch. The switch this replaced held a third copy of the funnel's shape beside
 * the table and the partition, and it had already drifted out of agreement with
 * the partition at five of eleven states. Asking the two authorities is how the
 * answer stays true when either changes: a new event that nobody classified is
 * already a type error on `EVENT_WITNESS`, and a new edge is picked up here for
 * free.
 *
 * A CALLER event always wins over a platform one when a state admits both — the
 * caller has a move, so telling it to wait would be false. No state admits both
 * today; the rule is stated so the derivation does not depend on that staying so.
 */
export function nextFunnelMove(state: OnboardingStateT): NextFunnelMove {
  const forward = forwardEvents(state);
  const caller = forward.find((e) => EVENT_WITNESS[e] === OnboardingEventWitness.Caller);
  if (caller !== undefined) return { kind: 'caller', event: caller };

  const waiting = forward[0];
  if (waiting === undefined) return { kind: 'complete' };
  return {
    kind: 'platform_witnessed',
    waiting_on: waiting,
    // Non-null by construction for every platform-witnessed event
    // ({@link PLATFORM_EVENT_PRODUCER} is total over them, pinned by
    // `onboarding.test.ts`). Coalesced rather than asserted so a future event
    // classified platform-side without a producer degrades to a plain wait
    // instead of throwing inside a state READ.
    instead: producerForPlatformEvent(waiting) ?? 'wait for the platform to observe it',
  };
}

/** Convenience: is the account fully onboarded? */
export function isComplete(state: OnboardingStateT): boolean {
  return state === OnboardingState.Active;
}

/** The terminal verdict of validating a repo's `.maintainer.yml` (#896). */
export type YmlVerdict = 'validated' | 'invalid';

/**
 * The ordered event(s) that drive an account toward the terminal onboarding
 * outcome for a `.maintainer.yml` verdict (#896) — the sequence the policy-ingest
 * consumer applies (each as a CAS) once it has validated the committed file. The
 * transition table alone can't express "a fixed file after a bad one": that path
 * is `INVALID --yml_retried--> YML_REQUESTED`, then `yml_validated` once the MCP
 * rung is settled, which this returns as far as the table allows.
 *
 * - `validated` from `MCP_CONNECTED`  → `[yml_validated]` (→ ACTIVE)
 * - `validated` from `YML_REQUESTED`  → `[]` — DEFERRED (#3632): ACTIVE is legal
 *   only from `MCP_CONNECTED`, and this function cannot assert a chat client. The
 *   verdict is not lost: `apps/api`'s read-path settle re-derives it from the
 *   durable policy snapshot (`hasValidatedPolicy`) after the MCP rung settles.
 * - `validated` from `REPOS_SELECTED` → `[yml_requested]` (the merged onboarding
 *   PR proves the file was asked for; the rest is the deferral above)
 * - `validated` from `INVALID`        → `[yml_retried]` (retry; the rest is deferred)
 * - `invalid`   from `YML_REQUESTED`  → `[yml_invalid]` (→ INVALID)
 * - `invalid`   from `MCP_CONNECTED`  → `[yml_invalid]` (→ INVALID)
 * - `invalid`   from `REPOS_SELECTED` → `[yml_requested, yml_invalid]` (→ INVALID)
 * - anything else (already `ACTIVE`, a re-push while `INVALID`, or a yml pushed
 *   before the funnel reached `REPOS_SELECTED`) → `[]` — a no-op, so a redelivery
 *   or an out-of-order push never forces an invalid transition.
 *
 * The asymmetry between the two verdicts is the point: a REFUSAL is taken from
 * every state that can hold a `.maintainer.yml`, because `INVALID` is the one
 * card that names the broken repo and delaying it behind an unrelated step
 * deletes the refusal; a SUCCESS waits, because it ends the funnel.
 *
 * WHY `REPOS_SELECTED` IS AN ORIGIN (arc 101 slice 05 step 6). There were two
 * `.maintainer.yml` paths and neither knew about the other: the funnel's step 6
 * card told the maintainer to write the file themselves, while the repo-onboarding
 * pass PROPOSES that same file in the `dz/onboarding` pull request minted on first
 * enable (`apps/runner/src/modes/setup/base/maintainer.ts` `renderMaintainerPolicy`, pinned to
 * human-merge). The card now advertises that PR — and merging it is a default-branch
 * push, so it lands here. Without this origin the advertised path completed nothing:
 * a maintainer who merged the PR from `REPOS_SELECTED` (the state prod holds, and the
 * one the install webhook once stamped on an install-created account) got `[]`, and the
 * only card left told them to merge a pull request that was already merged.
 *
 * `yml_requested` is caller-witnessed, and firing it from a platform driver is
 * deliberate: {@link witnessFor} partitions the CALLER-FACING door
 * (`POST /v1/onboarding/advance`), not this function — the `INVALID` pair has fired
 * caller-witnessed `yml_retried` the same way since #896. What the hop records is
 * "a `.maintainer.yml` was asked for", and the platform asking on the maintainer's
 * behalf is exactly as true as the maintainer copying a prompt.
 *
 * The `invalid` verdict gets the same origin for symmetry, and for the same reason
 * the panel table exists: a repo that already carried a broken `.maintainer.yml` is
 * never clobbered by the pass, so the refusal is reachable from this state — and
 * `INVALID` names the offending repo and offers a retry, where the alternative is
 * silence on the one card that says "merge the PR".
 */
export function ymlVerdictEvents(from: OnboardingStateT, verdict: YmlVerdict): OnboardingEvent[] {
  if (verdict === 'invalid') {
    if (from === OnboardingState.YmlRequested || from === OnboardingState.McpConnected) {
      return [OnboardingEvent.YmlInvalid];
    }
    if (from === OnboardingState.ReposSelected) {
      return [OnboardingEvent.YmlRequested, OnboardingEvent.YmlInvalid];
    }
    return [];
  }
  if (from === OnboardingState.McpConnected) return [OnboardingEvent.YmlValidated];
  if (from === OnboardingState.ReposSelected) return [OnboardingEvent.YmlRequested];
  if (from === OnboardingState.Invalid) return [OnboardingEvent.YmlRetried];
  return [];
}

// ─── dwell + stall ──────────────────────────────────────────────────────────
//
// The transition table above is PURE and timeless: it says which hops are legal
// and nothing about how long a row waited between them. That was the whole gap.
// Measured on prod 2026-09-02: 7 accounts, **4 of them still `NEW`**, the oldest
// created 2026-07-09 — 55 days in the funnel's first state — and `audit_events`
// held ZERO `onboarding.*` rows of any kind, all-time. So there was no way, from
// inside the platform, to tell "nobody signed up this week" from "the funnel is
// broken and four tenants are wedged in step 1". Both read as silence.
//
// A funnel with no dwell signal cannot be graded, so the signal comes first and
// the alarm hangs off it:
//
//   1. `accounts.onboarding_state_at` (migration `0182`) — stamped by the ONE
//      compare-and-swap writer, `saveOnboardingState`. NULL means no transition
//      has ever been observed for that row, which is exactly true of every row
//      that predates the column and every account that has not moved since.
//   2. {@link dwellFor} turns that stamp (plus `accounts.created_at`) into a
//      DISCRIMINATED dwell, so "we do not know" is a distinct answer from "0s".
//   3. {@link isStalled} grades it against {@link ONBOARDING_STALL_AFTER_MS}.
//
// The dwell rides every `onboarding.advanced` audit row (`apps/api`), and the
// stall is re-stated daily by the ops digest (`apps/worker/src/cron/ops-digest.ts`)
// for as long as it holds — level-triggered, like every other section there.

/**
 * How long an account may sit in ONE non-terminal onboarding state before the
 * platform calls it a stall.
 *
 * Seven days, and the number is the funnel's own: every remaining step is a
 * single click or a single commit, and the longest one (install the App, then
 * pick repos) is bounded by a maintainer's working week. A tenant that has not
 * moved across a full week is not "still deciding" — nothing is coming without
 * a human. Deliberately not shorter: a weekend plus a sick day must not page.
 *
 * NOT a cost cap and not a deadline — nothing is cancelled, refused or
 * downgraded when it trips. It only decides when the funnel stops being silent.
 */
export const ONBOARDING_STALL_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * WHERE a dwell's start time came from. Load-bearing rather than cosmetic: the
 * two sources do not carry the same confidence, and a reader that cannot tell
 * them apart will treat a bound as a measurement.
 *
 * - `transition` — `accounts.onboarding_state_at`, written by the CAS the moment
 *   the row entered this state. EXACT.
 * - `account_created` — `accounts.created_at`, used only when the stamp is NULL.
 *   EXACT when the state is `NEW`, because `NEW` is the state the ONE inserting
 *   writer stamps (`upsertAccountByGithubId`, `@developerz/db`, #3841, which on
 *   a match writes `github_login` alone and so cannot move an existing row) — an
 *   unstamped `NEW` row has therefore been `NEW` since it was created. From any other state it is an UPPER BOUND: the row reached that
 *   state at or after creation, so the dwell is at most this. A bound only ever
 *   over-states a stall, never hides one, which is the safe direction for an
 *   alarm and the wrong direction for a metric — so it is labelled, not averaged.
 */
export const ONBOARDING_DWELL_BASES = ['transition', 'account_created'] as const;
export type OnboardingDwellBasis = (typeof ONBOARDING_DWELL_BASES)[number];

/**
 * How long a row has been in its current onboarding state.
 *
 * A discriminated verdict, not `number | null`: a NULL dwell would collapse
 * "this row entered the state a moment ago" into "nothing recorded when this row
 * entered the state", and those demand opposite reactions. `known: false` is the
 * state of every account on the day the column ships and of every account whose
 * only writer never ran — an honest absence, countable and gone by the first hop.
 */
export type OnboardingDwell =
  | { known: true; basis: OnboardingDwellBasis; sinceIso: string; ms: number }
  | { known: false; reason: 'never_stamped' };

/**
 * Compute the dwell for a row from what the database actually holds. Pure — the
 * caller supplies the clock.
 *
 * `enteredAt` wins over `createdAt` whenever it is set; `createdAt` is the
 * documented fallback for an unstamped row (see {@link OnboardingDwellBasis} for
 * exactly when that is a measurement and when it is a bound). With neither, the
 * answer is `known: false` — never a fabricated zero.
 *
 * Clamped at 0: a stamp in the future (a clock skew between the app writing
 * `now()` and the reader's own clock) is a broken instrument, and a NEGATIVE
 * dwell would read downstream as "moved just now" — the one answer that
 * guarantees no alarm. Clamping keeps a skewed row gradeable instead.
 */
export function dwellFor(input: {
  enteredAt: Date | null;
  createdAt?: Date | null;
  now: Date;
}): OnboardingDwell {
  const from = input.enteredAt ?? input.createdAt ?? null;
  if (from === null) return { known: false, reason: 'never_stamped' };
  const basis: OnboardingDwellBasis = input.enteredAt === null ? 'account_created' : 'transition';
  return {
    known: true,
    basis,
    sinceIso: from.toISOString(),
    ms: Math.max(0, input.now.getTime() - from.getTime()),
  };
}

/**
 * Is `state` terminal — does the funnel accept NO event from it?
 *
 * DERIVED from {@link TRANSITIONS}, never a hard-coded `state === ACTIVE`. The
 * transition table is the one place that decides which hops exist, so asking it
 * is how this answer stays true when the table changes; a retyped equality is a
 * second seam that goes stale silently. `onboarding.test.ts` pins the property
 * that makes the derivation meaningful — `ACTIVE` is the ONLY terminal state, and
 * exactly one `(from, event)` pair in the whole table lands on it.
 */
export function isTerminal(state: OnboardingStateT): boolean {
  return Object.keys(TRANSITIONS[state]).length === 0;
}

/**
 * Has this account been sitting in one non-terminal state past
 * {@link ONBOARDING_STALL_AFTER_MS}?
 *
 * Terminal states are never stalled — an `ACTIVE` account is DONE, and grading it
 * against a clock would report every completed tenant as a defect forever. An
 * unknown dwell is never stalled either: an alarm on an absent measurement is an
 * alarm on the instrument, and the instrument's own gap is already countable
 * through `known: false`.
 */
export function isStalled(state: OnboardingStateT, dwell: OnboardingDwell): boolean {
  if (isTerminal(state)) return false;
  if (!dwell.known) return false;
  return dwell.ms >= ONBOARDING_STALL_AFTER_MS;
}
